/**
 * ComparisonBar — horizontal bar of N items (cities / regions / countries)
 * on a single numeric metric.  The current row can be highlighted in
 * teal to anchor the reader's location.
 *
 * Mobile strategy: `inline` for ≤ 8 items, `horizontal-scroll` for more.
 * The component itself handles both — it grows naturally and overflows
 * gracefully.
 */

import { cn } from '@/components/ui/cn';
import type { LangCode } from '../architecture';

export interface ComparisonRow {
  label: string;
  value: number;
  /** Optional one-liner shown as small print under the label. */
  note?: string;
  /** Highlights the row (used for "this country" / "current city"). */
  current?: boolean;
}

interface ComparisonBarProps {
  rows: ComparisonRow[];
  lang: LangCode;
  caption?: string;
  /** Suffix appended after each value (e.g. " °C", " %", " €"). */
  unit?: string;
  /** Fixed min/max for the scale; auto from data otherwise. */
  range?: [min: number, max: number];
  /** Decimal places (default 0). */
  decimals?: number;
}

export function ComparisonBar({
  rows,
  lang,
  caption,
  unit = '',
  range,
  decimals = 0,
}: ComparisonBarProps) {
  /* Group thousands, per locale.
   *
   * This used to be a bare `toFixed(decimals)`, which prints an airport's
   * 62663405 passengers as eight unbroken digits and Greece's 17320 as five.
   * Every other number the encyclopedia shows a reader goes through
   * facts.ts's Intl formatters; a widget row is the one place a figure
   * reached the page ungrouped. `decimals` still decides precision — this
   * only decides separators. */
  const fmt = new Intl.NumberFormat(lang === 'ru' ? 'ru-RU' : 'en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const values = rows.map((r) => r.value);
  const min = range?.[0] ?? Math.min(0, ...values);
  const max = range?.[1] ?? Math.max(...values);
  const span = max - min;

  return (
    <figure className="my-6 sm:my-8 rounded-lg border border-ink-100 bg-paper-2 p-4 sm:p-5">
      {caption && (
        <figcaption className="mb-3 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {caption}
        </figcaption>
      )}

      <ol
        className="space-y-2"
        aria-label={lang === 'ru' ? 'Сравнение по показателю' : 'Comparison'}
      >
        {rows.map((r, i) => {
          const pct = span === 0 ? 0 : ((r.value - min) / span) * 100;
          return (
            <li key={i} className="grid grid-cols-[minmax(80px,140px)_1fr_minmax(56px,max-content)] items-center gap-2 sm:gap-3">
              <span
                className={cn(
                  'min-w-0 truncate text-sm sm:text-base',
                  r.current ? 'font-semibold text-ink-900' : 'text-ink-700',
                )}
                title={r.note}
              >
                {r.label}
              </span>
              <span
                aria-hidden="true"
                className="relative h-5 overflow-hidden rounded bg-paper"
              >
                <span
                  className={cn(
                    'absolute inset-y-0 left-0 rounded motion-safe:transition-[width]',
                    r.current ? 'bg-terra-600' : 'bg-moss-500/70',
                  )}
                  style={{ width: `${Math.max(2, pct)}%` }}
                />
              </span>
              <span
                className={cn(
                  'text-right font-mono tabular-nums text-xs sm:text-sm',
                  r.current ? 'font-semibold text-terra-700' : 'text-ink-700',
                )}
              >
                {fmt.format(r.value)}{unit}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}
